import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, RefreshCw, Users, CheckCircle2, AlertTriangle, Wifi, WifiOff, Link2 } from "lucide-react";
import useEscClose from "../hooks/useEscClose";
import { api } from "../lib/api";
import { alertDialog, confirmDialog } from "../utils/dialogs";

/**
 * AttendanceSyncModal — pulls parent/guardian mobile numbers from the
 * I-Showed-Up attendance app (https://i-showed-up.ychyderabad.com) and
 * merges them into `sailor.baseline.family_contacts`. Admin-only.
 *
 * Flow:
 *   1. On open we check `/attendance-sync/status` to know whether creds
 *      are configured. If not we show a helpful "set env vars" panel.
 *   2. "Preview" runs a name-based fuzzy match against our roster and
 *      shows three sections: high-confidence auto-apply matches,
 *      low-confidence matches that need a coach review, and
 *      unmatched members (present in attendance app but not here).
 *   3. "Sync now" applies every high-confidence match plus any that the
 *      coach ticked in the low-confidence list.
 */
export default function AttendanceSyncModal({ open, onClose }) {
  useEscClose(open, onClose);
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [busy, setBusy] = useState("");
  const [checked, setChecked] = useState(() => new Set());
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setStatus(null); setStatusError(""); setPreview(null); setPreviewError("");
    setChecked(new Set()); setLastResult(null);
    api.get("/attendance-sync/status")
      .then((r) => { if (alive) setStatus(r.data); })
      .catch((e) => { if (alive) setStatusError(e?.response?.data?.detail || e?.message || "Could not read sync status"); });
    return () => { alive = false; };
  }, [open]);

  const runPreview = async () => {
    setBusy("preview"); setPreviewError(""); setPreview(null); setLastResult(null);
    try {
      const r = await api.post("/attendance-sync/preview");
      setPreview(r.data);
      // Auto-tick the high-confidence "changes" rows.
      const auto = new Set();
      (r.data.matches || []).forEach((m) => {
        if (m.status === "changes" && m.score >= 0.85) auto.add(m.sailor_id);
      });
      setChecked(auto);
    } catch (e) {
      setPreviewError(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally { setBusy(""); }
  };

  const applySelected = async () => {
    if (!preview) return;
    const rows = (preview.matches || []).filter((m) => m.status === "changes" && checked.has(m.sailor_id));
    if (rows.length === 0) {
      alertDialog("Tick at least one match to apply.");
      return;
    }
    if (!(await confirmDialog(
      `Write parent/guardian numbers for ${rows.length} athlete${rows.length === 1 ? "" : "s"}? ` +
      `Existing family contacts are merged (matching mobile numbers keep the coach's edits).`,
      { confirmLabel: "Apply" },
    ))) return;
    setBusy("apply");
    try {
      const r = await api.post("/attendance-sync/apply", {
        entries: rows.map((m) => ({
          sailor_id: m.sailor_id,
          member_id: m.member_id,
          member_name: m.member_name,
          contacts: m.contacts,
        })),
      });
      setLastResult({ applied: r.data.applied, at: r.data.at });
      // Refresh the preview so already-applied rows now show "unchanged".
      await runPreview();
    } catch (e) {
      alertDialog(e?.response?.data?.detail || e?.message || "Apply failed");
    } finally { setBusy(""); }
  };

  const toggle = (id) => {
    setChecked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (!open) return null;

  const cfg = status?.configured;
  const matches = preview?.matches || [];
  const changesMatches = matches.filter((m) => m.status === "changes");
  const unchangedMatches = matches.filter((m) => m.status === "unchanged");
  const unmatched = preview?.unmatched || [];

  return createPortal(
    <div
      className="fixed inset-0 z-[300] bg-[#040D1A]/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
      data-testid="attendance-sync-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="card w-full max-w-5xl p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Attendance sync</div>
            <h2 className="font-display text-xl text-slate-100 mt-0.5">Parent contacts from I-Showed-Up</h2>
            <div className="text-[12px] text-slate-400 mt-1 max-w-2xl">
              Pull guardian, mother and father mobile numbers from the attendance app so
              the WhatsApp share button on the Biometrics Trend can one-tap the whole family.
              Names are matched fuzzily — you can tick which suggested links to apply.
            </div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="attendance-sync-close">
            <X size={14} />
          </button>
        </div>

        {statusError && (
          <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-200 text-[12px] mb-3">
            {statusError}
          </div>
        )}

        {status && (
          <div className={`p-3 rounded-lg border text-[12px] mb-3 flex items-start gap-2 ${
            cfg ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-200"
                : "bg-amber-500/10 border-amber-500/40 text-amber-200"
          }`}>
            {cfg ? <Wifi size={14} className="mt-0.5" /> : <WifiOff size={14} className="mt-0.5" />}
            <div className="min-w-0">
              {cfg ? (
                <>
                  <b>Connected</b> to <span className="font-mono">{status.base_url}</span>
                  {" "}via <b>{status.auth_mode}</b> auth.
                </>
              ) : (
                <>
                  <b>Not configured.</b> Ask the app owner to set one of the following in <span className="font-mono">/app/backend/.env</span> and restart the backend:
                  <ul className="mt-1.5 list-disc pl-4 space-y-0.5">
                    <li><span className="font-mono">ATTENDANCE_API_TOKEN=...</span> (preferred — bearer token from the attendance app)</li>
                    <li>OR <span className="font-mono">ATTENDANCE_ADMIN_EMAIL</span> + <span className="font-mono">ATTENDANCE_ADMIN_PASSWORD</span> (admin login)</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            className="pill"
            onClick={runPreview}
            disabled={!cfg || busy === "preview"}
            data-testid="attendance-sync-preview"
          >
            <RefreshCw size={12} className={busy === "preview" ? "animate-spin" : ""} />
            {busy === "preview" ? "Fetching…" : "Fetch & preview"}
          </button>
          {preview && (
            <button
              className="pill !bg-emerald-600/20 !border-emerald-500/40 !text-emerald-200 disabled:opacity-50"
              onClick={applySelected}
              disabled={busy === "apply" || checked.size === 0}
              data-testid="attendance-sync-apply"
            >
              <CheckCircle2 size={12} />
              {busy === "apply" ? "Applying…" : `Apply ${checked.size} selected`}
            </button>
          )}
          {preview && (
            <div className="text-[11px] text-slate-500 ml-auto font-mono">
              members: {preview.counts.members_total} ·
              needs sync: {preview.counts.matched} ·
              in sync: {preview.counts.unchanged} ·
              unmatched: {preview.counts.unmatched}
            </div>
          )}
        </div>

        {lastResult && (
          <div className="p-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/40 text-cyan-200 text-[12px] mb-3">
            Applied contacts for <b>{lastResult.applied}</b> athlete{lastResult.applied === 1 ? "" : "s"}.
          </div>
        )}

        {previewError && (
          <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-200 text-[12px] mb-3">
            {previewError}
          </div>
        )}

        {preview && (
          <div className="space-y-6">
            {/* Section 1: matches with changes */}
            <MatchesSection
              title="Suggested updates"
              subtitle="High-confidence name matches with new/updated contacts. Tick to apply."
              icon={<Link2 size={13} className="text-emerald-300" />}
              rows={changesMatches}
              checked={checked}
              toggle={toggle}
              emptyText="Nothing to sync — every matched athlete already has these contacts."
            />

            {/* Section 2: unmatched members from attendance app */}
            <UnmatchedSection rows={unmatched} />

            {/* Section 3: already in sync (collapsible-lite via muted style) */}
            {unchangedMatches.length > 0 && (
              <details className="text-[12px]">
                <summary className="cursor-pointer text-slate-400 py-1">
                  Already in sync ({unchangedMatches.length}) — click to view
                </summary>
                <div className="mt-2 space-y-1">
                  {unchangedMatches.map((m) => (
                    <div key={m.sailor_id} className="text-slate-500">
                      <b className="text-slate-300">{m.sailor_name}</b>
                      <span className="text-slate-600 mx-1">↔</span>
                      {m.member_name} <span className="font-mono text-slate-600">({m.score})</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function MatchesSection({ title, subtitle, icon, rows, checked, toggle, emptyText }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <h3 className="font-display text-sm text-slate-100">{title}</h3>
        <span className="text-[11px] text-slate-500">· {rows.length}</span>
      </div>
      <div className="text-[11px] text-slate-500 mb-2">{subtitle}</div>
      {rows.length === 0 ? (
        <div className="text-[12px] text-slate-500 italic">{emptyText}</div>
      ) : (
        <div className="border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="bg-[#0B1B2E] text-slate-400 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="p-2 text-center w-8"></th>
                <th className="p-2 text-left">Athlete</th>
                <th className="p-2 text-left">Attendance member</th>
                <th className="p-2 text-left">Contacts</th>
                <th className="p-2 text-right w-16">Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.sailor_id} className="border-t border-slate-800 hover:bg-[#0B1B2E]/50">
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={checked.has(m.sailor_id)}
                      onChange={() => toggle(m.sailor_id)}
                      data-testid={`attendance-sync-tick-${m.sailor_id}`}
                    />
                  </td>
                  <td className="p-2 text-slate-200">{m.sailor_name}</td>
                  <td className="p-2 text-slate-300">{m.member_name}</td>
                  <td className="p-2">
                    <div className="flex flex-wrap gap-1">
                      {(m.contacts || []).map((c, i) => (
                        <span
                          key={`${m.sailor_id}-c-${i}`}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 font-mono"
                          title={c.name || ""}
                        >
                          <span className="opacity-70 mr-1">{c.role}</span>
                          {c.mobile}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-2 text-right font-mono text-slate-500">{m.score.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UnmatchedSection({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <AlertTriangle size={13} className="text-amber-300" />
        <h3 className="font-display text-sm text-slate-100">In attendance app but not in our roster</h3>
        <span className="text-[11px] text-slate-500">· {rows.length}</span>
      </div>
      <div className="text-[11px] text-slate-500 mb-2">
        These members exist in the attendance app but couldn't be matched to any athlete here.
        Either they're staff / adults, or their name in one of the two apps needs correcting.
      </div>
      <div className="border border-slate-800 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-[#0B1B2E] text-slate-400 text-[10px] uppercase tracking-wider sticky top-0">
            <tr>
              <th className="p-2 text-left">Attendance member</th>
              <th className="p-2 text-left">Numbers</th>
              <th className="p-2 text-left">Closest athlete</th>
              <th className="p-2 text-right w-16">Score</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m, i) => (
              <tr key={`u-${i}`} className="border-t border-slate-800">
                <td className="p-2 text-slate-200">{m.member_name || <span className="text-slate-500 italic">(no name)</span>}</td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    {(m.contacts || []).map((c, ci) => (
                      <span
                        key={`u-${i}-c-${ci}`}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/10 border border-slate-500/30 text-slate-300 font-mono"
                      >
                        <span className="opacity-70 mr-1">{c.role}</span>
                        {c.mobile}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="p-2 text-slate-400">
                  {m.best_sailor_name || <span className="italic text-slate-600">no candidate</span>}
                </td>
                <td className="p-2 text-right font-mono text-slate-500">
                  {m.best_score ? m.best_score.toFixed(2) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Wrapper button component for the User Menu.
export function AttendanceSyncMenuItem({ onClick }) {
  return (
    <button
      className="w-full text-left px-2 py-2 rounded-md hover:bg-[#0E1A2E] text-sm text-slate-200 flex items-center gap-2"
      onClick={onClick}
      data-testid="attendance-sync-btn"
    >
      <Users size={14} className="text-emerald-300" /> Sync parent contacts from Attendance
    </button>
  );
}
